import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Who the signed-in login is, for the route guards (RoleBasedRoute, ProtectedRoute,
 * SmartRedirect): staff vs portal, role, status, and the linked account's type.
 *
 * Every guard used to re-read this from Firestore on each navigation and sent the user
 * to /login ("Failed to load user data") when the read stalled, which happens whenever
 * Firestore's live connection drops (laptop sleep, network switch, QUIC errors). A
 * signed-in user is never bounced for that now:
 * - the result is cached per uid for the life of the tab, so later navigations render
 *   at once and refresh it in the background;
 * - with nothing cached yet, a failed or stalled read shows the reconnecting screen and
 *   retries on its own.
 * The cache only steers routing; the Firestore rules still decide what data is readable.
 */
const LOAD_TIMEOUT_MS = 15000;
const RETRY_DELAY_MS = 5000;

const cache = new Map(); // uid -> identity
const inflight = new Map(); // uid -> Promise<identity>

async function fetchIdentity(uid) {
  const internalSnap = await getDoc(doc(db, 'internal_users', uid));
  if (internalSnap.exists()) {
    const d = internalSnap.data();
    return { uid, userType: 'internal', role: d.role ?? null, status: d.status ?? null, onboardingStatus: null, accountType: null };
  }

  const userSnap = await getDoc(doc(db, 'users', uid));
  if (!userSnap.exists()) {
    return { uid, userType: null, role: null, status: null, onboardingStatus: null, accountType: null };
  }
  const d = userSnap.data();
  let accountType = null;
  if (d.accountId) {
    try {
      const accountSnap = await getDoc(doc(db, 'accounts', d.accountId));
      if (accountSnap.exists()) accountType = accountSnap.data().type ?? null;
    } catch (error) {
      console.error('Error fetching linked account:', error);
    }
  }
  return {
    uid,
    userType: 'portal',
    role: d.role ?? null,
    status: d.status ?? null,
    onboardingStatus: d.onboardingStatus ?? null,
    accountType,
  };
}

function loadIdentity(uid) {
  if (inflight.has(uid)) return inflight.get(uid);
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('User data fetch timeout')), LOAD_TIMEOUT_MS);
  });
  const promise = Promise.race([fetchIdentity(uid), timeout])
    .then((identity) => {
      cache.set(uid, identity);
      return identity;
    })
    .finally(() => {
      clearTimeout(timer);
      inflight.delete(uid);
    });
  inflight.set(uid, promise);
  return promise;
}

/** Drop cached identities (call on sign-out). */
export function clearRouteIdentityCache() {
  cache.clear();
}

/**
 * @returns {{ identity: object|null, loading: boolean, connectionTrouble: boolean, retry: () => void }}
 * `loading` is true until there is an identity for the current login (cached or fresh);
 * `connectionTrouble` is true while that first load keeps failing.
 */
export default function useRouteIdentity(currentUser, authLoading) {
  const uid = currentUser?.uid || null;
  const [identity, setIdentity] = useState(() => (uid ? cache.get(uid) || null : null));
  const [trouble, setTrouble] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (authLoading || !uid) return undefined;
    let active = true;
    let retryTimer;

    const cached = cache.get(uid);
    if (cached) setIdentity(cached);

    loadIdentity(uid)
      .then((fresh) => {
        if (!active) return;
        setIdentity(fresh);
        setTrouble(false);
      })
      .catch((error) => {
        console.warn('Could not load user data, retrying:', error);
        if (!active || cache.has(uid)) return;
        setTrouble(true);
        retryTimer = setTimeout(() => setAttempt((n) => n + 1), RETRY_DELAY_MS);
      });

    return () => {
      active = false;
      clearTimeout(retryTimer);
    };
  }, [uid, authLoading, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const current = identity && identity.uid === uid ? identity : null;

  return {
    identity: current,
    loading: authLoading || (!!uid && !current),
    connectionTrouble: !!uid && !current && trouble,
    retry,
  };
}
